import logger from "@/core/logger"
import { getVariations } from "../variations"
import { checkSearchMetadata, getEventProperties } from "./helpers"
import { reportAnalytics } from "./api"
import { getMetadataCache, keywordQueriesCache } from "./cache"
import bus from "@/core/api/bus"
import { assertValidClick, assertValidType } from "./validation"
import { SearchHit, SearchTrackOptions } from "./types"
import createAttribution from "@/core/api/event/attribution"
import { VIEW_PRODUCT } from "@/core/event"
import { isFeatureEnabled } from "./utils"
import { ValidationError } from "@/utils/error"

/**
 * Record search click event
 */
export function recordSearchClick(type: SearchTrackOptions, hit: SearchHit) {
  if (type === "autocomplete" && "keyword" in hit) {
    if (hit.keyword) {
      keywordQueriesCache.push(hit.keyword)
    }
    logger.info("Skipping search click event for autocomplete with keyword", hit.keyword)
    return
  }
  if (!isFeatureEnabled(type)) {
    logger.warn(new ValidationError(`${type} is not enabled in settings`))
    return
  }
  assertValidType(type)
  assertValidClick(hit)
  recordAttribution(type, hit, "click")
  return Promise.resolve()
}

export async function recordSearchAddToCart(type: SearchTrackOptions, hit: SearchHit) {
  if (type === "autocomplete" && "keyword" in hit) {
    logger.info("Skipping search click event for autocomplete with keyword", hit.keyword)
    return
  }
  if (!isFeatureEnabled(type)) {
    logger.warn(new ValidationError(`${type} is not enabled in settings`))
    return
  }
  assertValidType(type)
  assertValidClick(hit)
  const resultId = recordAttribution(type, hit, "addtocart")

  if (resultId) {
    // create VP event directly
    await createAttribution().recordAttribution({ type: VIEW_PRODUCT, target: hit.productId, ref: resultId }).done()
  }
}

function recordAttribution(type: SearchTrackOptions, hit: SearchHit, mode: "click" | "addtocart") {
  if (type === "category") {
    return recordCategoryAttribution(hit, mode)
  } else {
    return recordSearchAttribution(type, hit, mode)
  }
}

function recordCategoryAttribution(hit: SearchHit, mode: "click" | "addtocart") {
  const metadata = getMetadataCache("category").get()
  if (!metadata) {
    logger.warn(new ValidationError(`Category metadata not found. Was the category impression recorded?`))
    return
  }
  if (!metadata.category && !metadata.categoryId) {
    logger.warn(new ValidationError(`Category metadata is incomplete. Was the category impression recorded?`))
    return
  }

  const properties = getEventProperties(getVariations())
  const data = { productId: hit.productId, metadata, properties }
  bus.emit(`category${mode}`, data)
  reportAnalytics("category", "click", data)
}

function recordSearchAttribution(
  type: Exclude<SearchTrackOptions, "category">,
  hit: SearchHit,
  mode: "click" | "addtocart"
) {
  const metadata = getMetadataCache(type).get()
  if (!metadata) {
    logger.warn(new ValidationError(`${type} metadata not found. Was the search impression recorded?`))
    return
  }
  const properties = getEventProperties(getVariations())
  metadata.isAutoComplete = type === "autocomplete"
  const data = { productId: hit.productId, metadata, properties }
  bus.emit(`search${mode}`, data)
  const normalized = { ...data, metadata: checkSearchMetadata("click", data.metadata) }
  reportAnalytics("search", "click", normalized)
  return metadata.resultId
}
